"use client";

import { useEffect, useRef, useState } from "react";

import { Button, Group, RadioCard, RadioGroup } from "@mantine/core";
import { IconBrandZoom, IconSparkles } from "@tabler/icons-react";
import clsx from "clsx";

import { hasMotion } from "@/app/lib/motion/MotionScript";

import { RULE_KIND, RULE_STATUS, RuleKind, RuleState, RuleStatus } from "../rule";
import { useStage } from "../StageProvider";
import AppCard from "./AppCard";
import classes from "./ReviewView.module.css";
import StatusTag, { TAG_TONE, TagTone } from "./StatusTag";
import { WhereArrow, WhereLine, WhereSource } from "./WhereLine";

/** The rule's kinds, in the app's order, each with the force the tool prints
    for it (PRINCIPLE_KIND_FORCE in the backend's principle constants). The
    drawer shows the same force as a sentence. */
const RULE_KINDS: Record<RuleKind, { label: string; force: string }> = {
  [RULE_KIND.POLICY]: {
    label: "Policy",
    force: "follow this unless it has been formally overridden",
  },
  [RULE_KIND.HEURISTIC]: {
    label: "Heuristic",
    force: "the default approach unless there is a clear reason not to",
  },
  [RULE_KIND.BELIEF]: {
    label: "Belief",
    force: "what the firm believes to be true; weigh it, do not enforce it",
  },
};

const STATUS_TAGS: Record<RuleStatus, { label: string; tone: TagTone }> = {
  [RULE_STATUS.PROPOSED]: { label: "Proposed", tone: TAG_TONE.PROPOSED },
  [RULE_STATUS.CONFIRMED]: { label: "Confirmed", tone: TAG_TONE.CONFIRMED },
  [RULE_STATUS.RETIRED]: { label: "Retired", tone: TAG_TONE.SETTLED },
  [RULE_STATUS.DECLINED]: { label: "Declined", tone: TAG_TONE.SETTLED },
};

interface Verb {
  label: string;
  to: RuleStatus;
  isPrimary?: boolean;
  /** Confirming or reinstating a rule attests it, dated today. */
  attests?: boolean;
}

/** The verbs the app's principle service allows from each status. */
const VERBS: Record<RuleStatus, Verb[]> = {
  [RULE_STATUS.PROPOSED]: [
    { label: "Confirm & Publish", to: RULE_STATUS.CONFIRMED, isPrimary: true, attests: true },
    { label: "Decline", to: RULE_STATUS.DECLINED },
  ],
  [RULE_STATUS.CONFIRMED]: [{ label: "Retire", to: RULE_STATUS.RETIRED }],
  [RULE_STATUS.RETIRED]: [{ label: "Reinstate", to: RULE_STATUS.CONFIRMED, attests: true }],
  [RULE_STATUS.DECLINED]: [{ label: "Reopen", to: RULE_STATUS.PROPOSED }],
};

const RULE_ID = "principle_7Fq2kX9mRbT4wLcN8hVa3e";
const STATEMENT = "Nothing ships to customers on a Friday. If it has to, Dan signs it.";

/** The deploy agent's lookup before it acts. */
const AGENT_PROMPT = "deploy-agent ›";
const LOOKUP = 'find_principles "ship on Friday"';

/** The lookup re-runs after a verb: the old answer fades, and the new one
    arrives this long after the press. */
const ANSWER_SWAP_MS = 380;

/** The rest of the owner's queue: Maya's rule and three others while it is
    pending, the three others once it is not. */
function pendingCount(status: RuleStatus): number {
  return status === RULE_STATUS.PROPOSED ? 4 : 3;
}

function sentence(force: string): string {
  return `${force.charAt(0).toUpperCase()}${force.slice(1)}.`;
}

/** Today, as the tool prints an attested date. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
}

/** `find_principles` answering Dan's session, in the tool's own format: it
    returns confirmed rules only, and counts the rest of the queue. */
function LookupAnswer({ rule }: { rule: RuleState }) {
  const footer = `${pendingCount(rule.status)} proposal(s) awaiting confirmation are not listed; pass status="proposed" to see them.`;

  if (rule.status !== RULE_STATUS.CONFIRMED) {
    return <>No confirmed rules Dan can see match. {footer}</>;
  }

  const { force } = RULE_KINDS[rule.kind];

  return (
    <>
      {"1 confirmed rule(s) Dan can see, strongest binding force first:\n\n"}
      <span className={classes.dim}>
        {`- ${RULE_ID} · ${rule.kind} (${force}) · attested ${rule.attestedOn}`}
      </span>
      {`\n  ${STATEMENT}\n`}
      <span className={classes.dim}>{"  Applies to: Release"}</span>
      {"\n\n"}
      <span className={classes.dim}>{footer}</span>
    </>
  );
}

/**
 * Hands on: the app's review drawer for a rule said on a call, working. Maya
 * said it in the leadership sync and it waits in Pending; every verb does what
 * the app's does, and the deploy agent's `find_principles` lookup re-runs
 * after each one. Until someone confirms the rule, the agent that asks gets
 * nothing. The rule's state lives in the stage, so a layout flip keeps it.
 */
export default function ReviewView() {
  const { rule, setRule } = useStage();
  const [shownRule, setShownRule] = useState(rule);
  const [isRerunning, setIsRerunning] = useState(false);
  const firstVerb = useRef<HTMLButtonElement>(null);
  const hasPressedVerb = useRef(false);
  const swapTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(swapTimer.current), []);

  // The pressed verb leaves with its status, so focus moves to the verb that
  // replaced it.
  useEffect(() => {
    if (!hasPressedVerb.current) return;

    hasPressedVerb.current = false;
    firstVerb.current?.focus();
  }, [rule.status]);

  function press(verb: Verb) {
    const next: RuleState = {
      ...rule,
      status: verb.to,
      attestedOn: verb.attests ? today() : rule.attestedOn,
    };

    hasPressedVerb.current = true;
    setRule(next);
    window.clearTimeout(swapTimer.current);

    if (!hasMotion()) {
      setShownRule(next);
      return;
    }

    setIsRerunning(true);
    swapTimer.current = window.setTimeout(() => {
      setShownRule(next);
      setIsRerunning(false);
    }, ANSWER_SWAP_MS);
  }

  const status = STATUS_TAGS[rule.status];
  const isProposed = rule.status === RULE_STATUS.PROPOSED;

  return (
    <>
      <WhereLine>
        <WhereSource icon={<IconBrandZoom stroke={1.8} aria-hidden />}>Leadership sync</WhereSource>
        <WhereArrow />
        {/* Dim until the rule is confirmed: until then the agent that asks gets nothing. */}
        <WhereSource
          icon={<IconSparkles stroke={1.8} aria-hidden />}
          className={clsx(classes.agent, rule.status === RULE_STATUS.CONFIRMED && classes.agentLit)}
        >
          deploy-agent
        </WhereSource>
      </WhereLine>

      <AppCard title="Pending" aside={pendingCount(rule.status)}>
        <div className={classes.body}>
          <div className={classes.tags}>
            <StatusTag tone={status.tone}>{status.label}</StatusTag>
            {!isProposed && (
              <StatusTag tone={TAG_TONE.KIND}>{RULE_KINDS[rule.kind].label}</StatusTag>
            )}
          </div>

          <div className={classes.statement}>{STATEMENT}</div>
          <blockquote className={classes.quote}>
            from now on, nothing ships to customers on a Friday. If it has to, Dan signs it.
          </blockquote>

          <dl className={classes.facts}>
            <dt>Said By</dt>
            <dd>Maya</dd>
            <dt>Source</dt>
            <dd>Leadership sync</dd>
          </dl>

          {isProposed && (
            <RadioGroup
              value={rule.kind}
              onChange={(kind) => setRule({ ...rule, kind: kind as RuleKind })}
              label="How binding is this?"
              classNames={{ root: classes.kind, label: classes.kindLabel }}
            >
              <Group gap={6} className={classes.kindOptions}>
                {Object.entries(RULE_KINDS).map(([value, kind]) => (
                  <RadioCard key={value} value={value} className={classes.chip}>
                    <span className={classes.ring} aria-hidden />
                    {kind.label}
                  </RadioCard>
                ))}
              </Group>
              <p className={classes.force}>{sentence(RULE_KINDS[rule.kind].force)}</p>
            </RadioGroup>
          )}

          <Group gap={6} className={classes.verbs}>
            {VERBS[rule.status].map((verb, index) => (
              <Button
                key={verb.label}
                ref={index === 0 ? firstVerb : undefined}
                variant={verb.isPrimary ? "gradient" : "default"}
                gradient={{ from: "blue", to: "cyan", deg: 90 }}
                className={classes.verb}
                onClick={() => press(verb)}
              >
                {verb.label}
              </Button>
            ))}
          </Group>
        </div>

        <pre className={classes.transcript} aria-live="polite">
          <span className={classes.prompt}>{AGENT_PROMPT}</span> {LOOKUP}
          {"\n"}
          <span className={classes.answer} data-rerunning={isRerunning || undefined}>
            <LookupAnswer rule={shownRule} />
          </span>
        </pre>
      </AppCard>
    </>
  );
}
